import { LazyRoute } from "@appretio/shared";
import { ErrorBoundary, semanticThemeTokens, Sheet } from "@appretio/ui-kit";
import { lazy } from "react";
import { Outlet, type RouteObject } from "react-router-dom";

const BudgetPage = lazy(() =>
  import("./pages/budgetPage/BudgetPage").then((m) => ({
    default: m.BudgetPage
  }))
);

const BudgetSummaryPage = lazy(() =>
  import("./pages/budgetSummaryPage/BudgetSummaryPage").then((m) => ({
    default: m.BudgetSummaryPage
  }))
);

const BudgetManageCubesPage = lazy(() =>
  import("./pages/budgetManageCubesPage/BudgetManageCubesPage").then((m) => ({
    default: m.BudgetManageCubesPage
  }))
);

const ROOT = "pre-season" as const;

// Mirrors the in-season layout pattern: flat sibling routes under the
// `pre-season` root. Path repetition (e.g. `budget/budget`) keeps the URL
// shape symmetric with `in-season/in-season-forecast/forecast`.
const PATH = {
  BUDGET: "budget/budget",
  BUDGET_SUMMARY: "budget/summary",
  BUDGET_MANAGE_CUBES: "budget/manage-cubes",
  CONSUMER_ACTIVATION_PLAN: "consumer-activation-plan"
} as const;

const LABEL = {
  PRE_SEASON: "Pre-season",
  BUDGET: "Budget",
  BUDGET_SUMMARY: "Budget summary",
  MANAGE_CUBES: "Manage cubes",
  CONSUMER_ACTIVATION_PLAN: "Consumer Activation Plan"
} as const;

// Align Budget pages with Forecast's chrome shade:
// both modules now use `backgroundGradient[2]` (shared inner colour +
// the cool-blue outer).
const BG = semanticThemeTokens.backgroundGradient[2];

export const preSeasonRoutes: RouteObject = {
  path: ROOT,
  handle: { label: LABEL.PRE_SEASON, background: BG },
  element: <Outlet />,
  children: [
    {
      path: PATH.BUDGET,
      handle: { label: LABEL.BUDGET, background: BG },
      element: (
        <ErrorBoundary level="route">
          <LazyRoute>
            <BudgetPage />
          </LazyRoute>
        </ErrorBoundary>
      )
    },
    {
      path: PATH.BUDGET_SUMMARY,
      handle: { label: LABEL.BUDGET_SUMMARY, background: BG },
      element: (
        <ErrorBoundary level="route">
          <LazyRoute>
            <BudgetSummaryPage />
          </LazyRoute>
        </ErrorBoundary>
      )
    },
    {
      path: PATH.BUDGET_MANAGE_CUBES,
      handle: { label: LABEL.MANAGE_CUBES, background: BG },
      element: (
        <ErrorBoundary level="route">
          <LazyRoute>
            <BudgetManageCubesPage />
          </LazyRoute>
        </ErrorBoundary>
      )
    },
    {
      path: PATH.CONSUMER_ACTIVATION_PLAN,
      handle: { label: LABEL.CONSUMER_ACTIVATION_PLAN, background: BG },
      element: <Sheet>{LABEL.CONSUMER_ACTIVATION_PLAN} page</Sheet>
    }
  ]
};
